/**
 * /table · UI primitives (RouteChip · MemberRouteChip · VerifyLine).
 * Used by cards, results header, and the drawer.
 *
 * Card vs Drawer parity (K1.1 · founder ruling 14 Jun 2026 late):
 *   The drawer is where the honest, named booking ladder lives
 *   ("via Google Reserve", "SevenRooms", "Direct · Instant") · the
 *   member is committing to an action there and needs to see the
 *   actual rail. The CARD is the editorial preview · supplier names
 *   must not dominate the surface (parity with /escape's
 *   "Curated by Concierge®" / "Request via Concierge®" treatment).
 *
 *   `RouteChip` keeps its honest supplier label · drawer-only.
 *   `MemberRouteChip` ships the card-level Concierge®-led label.
 *   The two share the same routing classifier — only the visible
 *   text differs. Provider identity is preserved in
 *   `data-provider-kind` for admin/provenance / e2e tests.
 */
import React from "react";
import { ROUTE, routeVia, verifiedAt } from "./route-utils";

export function RouteChip({ route, small }) {
    const r = ROUTE[route] || ROUTE.concierge;
    return (
        <span
            data-testid={`route-chip-${route}`}
            className="inline-flex items-center gap-1.5"
            style={{
                background: r.wash,
                color: r.color,
                border: `1px solid ${r.color}33`,
                borderRadius: 30,
                padding: small ? "3px 9px" : "4px 11px",
                fontSize: small ? 11 : 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
            }}
        >
            <span style={{ fontSize: small ? 10 : 11 }}>{r.icon}</span>
            {r.label}
        </span>
    );
}

/**
 * K1.1 (founder ruling 14 Jun 2026 late · "Enjoy/Table card parity
 * with Escape"): the member-facing chip strips the supplier name.
 *
 *   • instant / google_reserve / call → "Reserve" (brass, ⚡)
 *   • contact                          → "View details" (slate, ✉)
 *   • concierge / hard-to-book         → "Concierge®" (brass, ✦)
 *
 * The booking rail still works behind the CTA · the chip is purely
 * visual hygiene. The original tier key is preserved in
 * `data-route-key` so admin/provenance tooling can still grep on it.
 */
const MEMBER_CHIP = {
    instant:        { label: "Reserve",        icon: "\u26a1", tone: "brass" },
    google_reserve: { label: "Reserve",        icon: "\u26a1", tone: "brass" },
    call:           { label: "Reserve",        icon: "\u26a1", tone: "brass" },
    contact:        { label: "View details",   icon: "\u2192", tone: "slate" },
    concierge:      { label: "Concierge\u00ae", icon: "\u2726", tone: "brass" },
    guest_list:     { label: "Concierge\u00ae", icon: "\u2726", tone: "brass" },
    table_service:  { label: "Reserve",        icon: "\u26a1", tone: "brass" },
};

export function MemberRouteChip({ route, small, providerKind }) {
    const cfg = MEMBER_CHIP[route] || MEMBER_CHIP.concierge;
    const isBrass = cfg.tone === "brass";
    const bg = isBrass ? "var(--t-brass-wash)" : "var(--t-slate-wash)";
    const fg = isBrass ? "var(--t-brass-deep, var(--t-brass))" : "var(--t-slate)";
    const border = isBrass ? "var(--t-brass2)" : "var(--t-slate2, #B4B4A8)";
    return (
        <span
            data-testid={`member-route-chip-${route}`}
            data-route-key={route}
            data-provider-kind={providerKind || undefined}
            className="inline-flex items-center gap-1.5"
            style={{
                background: bg,
                color: fg,
                border: `1px solid ${border}`,
                borderRadius: 30,
                padding: small ? "3px 9px" : "4px 11px",
                fontSize: small ? 11 : 12,
                fontWeight: 600,
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
                backdropFilter: "blur(8px)",
            }}
        >
            <span style={{ fontSize: small ? 10 : 11 }}>{cfg.icon}</span>
            {cfg.label}
        </span>
    );
}

export function VerifyLine({ venue, light }) {
    // Doctrine 7 Jun 2026 (post-Vienna verification breach):
    // The green ✓ "verified" is EARNED — by a human-entered anchor
    // or a human check this run. A live re-resolve alone (which is
    // all the old `verifiedAt` reflected) is necessary but NOT
    // sufficient: Google's `types` can be stale (Velani mis-typed
    // Indian) and Google's `weekday_text` can be stale (Gaia's
    // Tuesday closure missing). The card honestly reads "Concierge®
    // to verify" by default; the green check only appears when the
    // pipeline marks the venue concierge_confirmed.
    //
    // K1.1 (14 Jun 2026 late): when rendered on a card (`light` mode),
    // we DROP the leading `via {supplier}` segment so the member-
    // facing card doesn't read as a raw provider directory. The
    // drawer (non-light) keeps the honest supplier name because
    // that's the booking surface.
    const tier = venue?.provenance_tier;
    const isAnchor = Boolean(venue?._anchor_id || venue?.anchor_id);
    const conciergeConfirmed = tier === "concierge_confirmed" || isAnchor;
    const isConcierge = (venue?.booking_cta || "").includes("Concierge");
    const via = routeVia(venue);
    const showVia = !light && Boolean(via);
    return (
        <div
            data-testid="verify-line"
            data-provenance={conciergeConfirmed ? "concierge_confirmed" : "to_verify"}
            data-route-via={via || undefined}
            className="t-mono flex items-center gap-1.5"
            style={{
                fontSize: 10.5,
                color: light ? "var(--t-ink3)" : "var(--t-ink2)",
            }}
        >
            <span
                style={{
                    color: conciergeConfirmed
                        ? "var(--t-verified)"
                        : "var(--t-brass)",
                }}
            >
                {conciergeConfirmed ? "\u2713" : "\u25c7"}
            </span>
            {conciergeConfirmed ? (
                <span>
                    {showVia ? `${via} \u00b7 ` : ""}
                    {"confirmed by Concierge\u00ae"}
                </span>
            ) : isConcierge ? (
                <span>{verifiedAt(venue)}{" \u00b7 awaiting Concierge\u00ae"}</span>
            ) : light ? (
                // K1.2 (14 Jun 2026 evening · founder ruling "remove
                // google words from front of card"): card mode drops
                // the "per Google" provenance entirely · member sees a
                // clean "Concierge® confirms at booking" promise. The
                // drawer still carries the honest provenance line below.
                <span>{"the Concierge\u00ae confirms at booking"}</span>
            ) : (
                <span>
                    {showVia ? `${via} \u00b7 ` : ""}
                    {"per Concierge\u00ae \u00b7 the Concierge\u00ae confirms at booking"}
                </span>
            )}
        </div>
    );
}
